import { Button, Input, PasswordInput } from "components/ui/v2";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  NAME_VALIDATION_REGEX,
  NO_ORGANIZATION_REDIRECT,
  PASSWORD_REGEX,
  SELECTED_VENDOR,
  USER_TOKEN_PERSIST,
} from "@constants/global";
import { useUserStoreContext } from "context/userStore/userStore.provider";
import { gqlUser } from "gql";
import useTranslation from "next-translate/useTranslation";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import isEmail from "validator/lib/isEmail";

interface IRegisterFormProps {
  callback?: (error?: string) => void;
  invitationCode?: string;
  redirect?: string;
}

const RegisterForm = (props: IRegisterFormProps) => {
  const { callback, redirect, invitationCode } = props;

  const { setAccessToken } = useUserStoreContext((store) => ({
    setAccessToken: store.setAccessToken,
  }));

  const { t } = useTranslation("common");
  const {
    handleSubmit: handleRegisterSubmit,
    control: registerControl,
    formState: { errors },
    watch,
    setValue,
  } = useForm();
  const router = useRouter();
  const { tkn, email: rawEmail } = router.query;

  // * check if i receive and email with space me groowcityuser@simonguest.com
  const email = rawEmail?.toString().replace(/ /g, "+");

  const [getUser, { data: getUserData }] = useLazyQuery(
    gqlUser.queries.GET_USER
  );

  const [signup, { error: signupError, loading }] = useMutation(
    gqlUser.mutations.SIGNUP,
    {
      onCompleted({ signup }) {
        const { accessToken } = signup;
        localStorage.setItem(USER_TOKEN_PERSIST, `${accessToken}`);
        setAccessToken(accessToken);

        getUser({
          variables: {
            accessToken,
          },
        });
      },
      onError: ({ message }) => {
        if (callback) {
          callback(message);
        }
      },
    }
  );

  useEffect(() => {
    if (getUserData) {
      const {
        user: { selectedVendorId, vendorList },
      } = getUserData;

      localStorage.setItem(SELECTED_VENDOR, `${selectedVendorId}`);

      if (callback) {
        callback();
        return;
      }

      // * If the user came from an invitation link, redirect to inv
      if (redirect) {
        router.push({
          pathname: redirect,
          query: {
            t: invitationCode,
          },
        });
        return;
      }

      if (!vendorList?.length) {
        router.push(NO_ORGANIZATION_REDIRECT);
      }
    }
  }, [getUserData]);

  useEffect(() => {
    if (email) {
      setValue("email", email);
    }
  }, [email]);

  const handleRegister = async (data) => {
    if (typeof window !== "undefined") {
      try {
        await signup({
          variables: {
            signUpInput: {
              name: data.name,
              email: data.email,
              password: data.password,
              registerTkn: tkn,
            },
          },
        });
      } catch (error) {
        console.error(error);
      }
    }
  };

  return (
    <div className="grid">
      <form onSubmit={handleRegisterSubmit(handleRegister)} className="flex flex-col gap-4 px-1 py-1">

        <Controller
          name="name"
          defaultValue=""
          control={registerControl}
          rules={{
            required: true,
            pattern: {
              value: NAME_VALIDATION_REGEX,
              message: t(
                "Al menos una letra y nada de cáracteres especiales 🤔"
              ),
            },
          }}
          render={({ field }) => (
            <Input
              {...field}
              label={t("Nombre")}
              placeholder={t("ej: Elon Musk")}
              error={
                errors?.name
                  ? errors.name.type === "pattern"
                    ? errors.name.message as string
                    : t("Debes ingresar tu nombre")
                  : undefined
              }
            />
          )}
        />

        <Controller
          name="email"
          defaultValue=""
          control={registerControl}
          rules={{
            required: true,
            validate: (value) => isEmail(value),
          }}
          render={({ field }) => (
            <Input
              {...field}
              type="email"
              label={t("Correo electrónico")}
              placeholder={t("ej: elon@groowcity.com")}
              disabled={!!tkn}
              error={errors?.email ? t("Debes ingresar un e-mail válido") : undefined}
            />
          )}
        />

        <div className="space-y-1.5">
        <Controller
          name="password"
          control={registerControl}
          defaultValue=""
          rules={{
            required: true,
            pattern: PASSWORD_REGEX,
          }}
          render={({ field: { ...rest } }) => (
            <PasswordInput
              {...rest}
              label={t("Escribe tu contraseña")}
              placeholder={t("Escribe tu contraseña")}
            />
          )}
        />

        <small className="w-full text-[11px] text-gray-500">
          <span
            className={
              watch("password")?.length >= 8
                ? "text-green-500"
                : "text-gray-500"
            }
          >
            {t("Mínimo 8 caracteres")}
          </span>{" "}
          ·{" "}
          <span
            className={
              watch("password")?.match(/[A-Z]/g)
                ? "text-green-500"
                : "text-gray-500"
            }
          >
            {t("1 mayúscula")}
          </span>{" "}
          ·{" "}
          <span
            className={
              watch("password")?.match(/[0-9]/g)
                ? "text-green-500"
                : "text-gray-500"
            }
          >
            {t("1 número")}
          </span>
        </small>

        </div>

        {errors?.password?.type === "required" && (
          <small className="w-full text-red-500">
            {t("Debes ingresar una contraseña.")}
          </small>
        )}

        {errors?.password?.type === "pattern" && (
          <small className="w-full text-red-500">
            {t(
              "La contraseña debe tener al menos 1 número, 1 mayúscula y al menos 8 caracteres"
            )}
          </small>
        )}

        <div className="space-y-1.5">
        <Controller
          name="password_repeat"
          control={registerControl}
          defaultValue=""
          rules={{
            required: true,
            minLength: 8,
            validate: (value) =>
              value === watch("password") || t("Las contraseñas no coinciden"),
          }}
          render={({ field: { ...rest } }) => (
            <PasswordInput
              {...rest}
              label={t("Repite tu contraseña")}
              placeholder={t("Repite tu contraseña")}
            />
          )}
        />
        {errors && errors.password_repeat && (
          <small className="w-full text-[9px] text-red-500">
            {t("Contraseñas no son iguales")}
          </small>
        )}
        </div>
        <div className="mt-2 flex justify-center">
          <Button
            type="submit"
            className="w-full md:w-1/2"
            loading={loading}
          >
            {t("Crear tu cuenta")}
          </Button>
        </div>
        {signupError && (
          <p className="mt-1 w-full text-right text-[9px] text-red-500">
            {signupError.message}
          </p>
        )}
      </form>
    </div>
  );
};

export default RegisterForm;
